import { useState, useRef, useEffect } from "react";
import { Calendar } from "lucide-react";
import { ArrowDropDown } from "@mui/icons-material";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";

interface DateFilterProps {
  onFilterApply: (initialDate: Date, finalDate: Date, label: string) => void;
}

type TabType = "relative" | "specific";

export const DateFilter: React.FC<DateFilterProps> = ({ onFilterApply }) => {
  const { language } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("relative");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getMaxDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const maxDate = getMaxDate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleRelativeFilter = (days: number, label: string) => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - days);
    onFilterApply(pastDate, today, label);
    setIsOpen(false);
  };

  const handleApplySpecificFilter = () => {
    if (!startDate || !endDate) return;
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    const initialDate = new Date(startDate);
    const finalDate = new Date(endDate);
    
    if (finalDate > today) {
      finalDate.setTime(today.getTime());
    }
    
    if (initialDate > finalDate) {
      return;
    }
    
    const label = `${startDate} - ${endDate}`;
    onFilterApply(initialDate, finalDate, label);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 border border-gray-100 rounded-xl bg-white hover:bg-gray-50 transition-colors ${
          isOpen ? 'text-black' : 'text-gray-500'
        }`}
        style={{
          height: '48px',
          WebkitTextFillColor: isOpen ? 'black' : '#6B7280',
          border: isOpen ? '1px solid #8284F3' : '1px solid #C8C8C8',
        }}
      >
        <Calendar className={`w-5 h-5 ${isOpen ? 'text-gray-700' : 'text-gray-400'}`} />
        <span className="font-medium flex-1">
          {dictionary["filter-by-date"]?.[language] || "Filtrar por fecha"}
        </span>
        <ArrowDropDown 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          sx={{ 
            fontSize: '20px',
            color: isOpen ? '#374151' : '#9CA3AF'
          }}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full mt-2 left-0 w-[400px] bg-white rounded-2xl shadow-xl border border-gray-200 z-50">
          {/* Tabs */}
          <div className="flex gap-2 p-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("relative")}
              className={`flex-1 px-3 py-3 rounded-xl font-medium transition-all ${
                activeTab === "relative"
                  ? "bg-[#E8EAFF] text-[#4D5FE8]"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {dictionary["relative-dates"]?.[language] || "Fechas relativas"}
            </button>
            <button
              onClick={() => setActiveTab("specific")}
              className={`flex-1 px-3 py-3 rounded-xl font-medium transition-all ${
                activeTab === "specific"
                  ? "bg-[#E8EAFF] text-[#4D5FE8]"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {dictionary["specific-dates"]?.[language] || "Fechas específicas"}
            </button>
          </div>

          {/* Contenido según tab activo */}
          <div className="p-4">
            {activeTab === "relative" ? (
              <div className="space-y-2">
                <button
                  onClick={() => handleRelativeFilter(0, dictionary["today"]?.[language] || "Hoy")}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["today"]?.[language] || "Hoy"}
                </button>
                <button
                  onClick={() => handleRelativeFilter(1, dictionary["yesterday"]?.[language] || "Ayer")}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["yesterday"]?.[language] || "Ayer"}
                </button>
                <button
                  onClick={() => handleRelativeFilter(7, dictionary["last-7-days"]?.[language] || "Últimos 7 días")}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["last-7-days"]?.[language] || "Últimos 7 días"}
                </button>
                <button
                  onClick={() => handleRelativeFilter(30, dictionary["last-30-days"]?.[language] || "Últimos 30 días")}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["last-30-days"]?.[language] || "Últimos 30 días"}
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    onFilterApply(firstDay, today, dictionary["this-month"]?.[language] || "Este mes");
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["this-month"]?.[language] || "Este mes"}
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                    onFilterApply(firstDayLastMonth, lastDayLastMonth, dictionary["last-month"]?.[language] || "Mes pasado");
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {dictionary["last-month"]?.[language] || "Mes pasado"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {dictionary["start-date"]?.[language] || "Fecha inicial"}
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        setStartDate(selectedDate);
                        if (selectedDate && endDate && selectedDate > endDate) {
                          setEndDate(selectedDate);
                        }
                      }}
                      max={maxDate}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4D5FE8] focus:border-transparent"
                      placeholder="dd/mm/yyyy"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {dictionary["end-date"]?.[language] || "Fecha final"}
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        setEndDate(selectedDate);
                        if (selectedDate && startDate && selectedDate < startDate) {
                          setStartDate(selectedDate);
                        }
                      }}
                      max={maxDate}
                      min={startDate || undefined}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4D5FE8] focus:border-transparent"
                      placeholder="dd/mm/yyyy"
                    />
                  </div>
                </div>
                <button
                  onClick={handleApplySpecificFilter}
                  disabled={!startDate || !endDate}
                  className="w-full py-3 bg-[#8B91FF] text-white rounded-lg font-medium hover:bg-[#7780FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {dictionary["apply-filter"]?.[language] || "Aplicar filtro"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;

